import { useState, useSyncExternalStore } from 'react'
import {
  getDefaultTimezone,
  getFallbackTimezone,
  readBrowserTimezone,
  setDefaultTimezone,
  setFallbackTimezone
} from 'utils/timezone'

function createTimezoneStore() {
  let settings = {
    timeZone: getDefaultTimezone(),
    isFallback: readBrowserTimezone() === undefined
  }
  const listeners = new Set<() => void>()

  const applyTimezone = (zone: string, isFallback: boolean) => {
    if (zone !== getDefaultTimezone()) setDefaultTimezone(zone)
    if (settings.timeZone !== zone || settings.isFallback !== isFallback) {
      settings = { timeZone: zone, isFallback }
      listeners.forEach((listener) => listener())
    }
  }

  const checkSystemTimezone = () => {
    const detected = readBrowserTimezone()
    applyTimezone(detected ?? getFallbackTimezone(), detected === undefined)
  }

  const setTimeZone = (zone: string) => {
    // Recheck here, too: an open picker must not override recovered detection.
    const detected = readBrowserTimezone()
    if (detected) applyTimezone(detected, false)
    else if (setFallbackTimezone(zone)) applyTimezone(zone, true)
  }

  const subscribe = (listener: () => void) => {
    const onVisibilityChange = () => {
      if (!document.hidden) checkSystemTimezone()
    }
    listeners.add(listener)
    window.addEventListener('focus', checkSystemTimezone)
    document.addEventListener('visibilitychange', onVisibilityChange)
    // Close the gap between the initial reading and subscribing.
    checkSystemTimezone()
    return () => {
      listeners.delete(listener)
      window.removeEventListener('focus', checkSystemTimezone)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }

  return { subscribe, getSnapshot: () => settings, setTimeZone }
}

export function useSystemTimezone() {
  const [store] = useState(createTimezoneStore)
  const settings = useSyncExternalStore(store.subscribe, store.getSnapshot)
  return [settings.timeZone, store.setTimeZone, settings.isFallback] as const
}
